/* Shared, display-only countdown for product and promotional sections. */
(() => {
  'use strict';
  if (customElements.get('pdp-countdown')) return;

  function fixedDeadline(value) {
    // The editor uses a human-readable UTC value. Keep ISO 8601 support for
    // existing product countdowns saved before this control was clarified.
    const utc = /^(\d{2})\.(\d{2})\.(\d{4}),?\s+(\d{2}):(\d{2})\s+UTC$/i.exec((value || '').trim());
    if (utc) {
      const [, day, month, year, hour, minute] = utc.map(Number);
      if (hour > 23 || minute > 59) return NaN;
      const epoch = Date.UTC(year, month - 1, day, hour, minute);
      const actual = new Date(epoch);
      return actual.getUTCFullYear() === year && actual.getUTCMonth() + 1 === month && actual.getUTCDate() === day ? epoch : NaN;
    }
    // Shopify may retain a previously saved one-digit offset (+1:00). Normalize
    // it before parsing instead of silently hiding a valid merchant deadline.
    const normalized = (value || '').trim().replace(/([+-])(\d):(\d{2})$/, (_, sign, hour, minute) => `${sign}0${hour}:${minute}`);
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/.exec(normalized);
    if (!match) return NaN;
    const [, year, month, day, hour, minute, second = '0', zone] = match;
    const epoch = Date.parse(normalized);
    const offset = zone === 'Z' ? 0 : (zone[0] === '+' ? 1 : -1) * (Number(zone.slice(1, 3)) * 60 + Number(zone.slice(4, 6)));
    const wall = new Date(epoch + offset * 60000);
    if (Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59 || !Number.isFinite(epoch)) return NaN;
    if (wall.getUTCFullYear() !== Number(year) || wall.getUTCMonth() + 1 !== Number(month) || wall.getUTCDate() !== Number(day)) return NaN;
    return epoch;
  }

  function nextMonth(now, requestedZone) {
    const timeZone = requestedZone === 'visitor' ? Intl.DateTimeFormat().resolvedOptions().timeZone : requestedZone;
    const formatter = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
    const parts = date => Object.fromEntries(formatter.formatToParts(date).filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
    const current = parts(now);
    const target = Date.UTC(current.year, current.month, 1);
    let epoch = target;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const actual = parts(epoch);
      const correction = target - Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second);
      epoch += correction;
      if (!correction) return epoch;
    }
    return NaN;
  }

  class PDPCountdown extends HTMLElement {
    static get observedAttributes() { return ['data-mode', 'data-deadline', 'data-timezone']; }
    attributeChangedCallback() { if (this.isConnected && this.started) this.connectedCallback(); }
    connectedCallback() {
      this.cleanup?.();
      this.started = true;
      const digits = this.querySelector('[data-countdown-digits]');
      const expired = this.querySelector('[data-countdown-expired]');
      if (!digits || !expired) return;
      let deadline;
      try {
        deadline = this.dataset.mode === 'monthly' ? nextMonth(Date.now(), this.dataset.timezone || 'visitor') : fixedDeadline(this.dataset.deadline);
      } catch {
        deadline = NaN;
      }
      let timer;
      const update = () => {
        clearTimeout(timer);
        if (!Number.isFinite(deadline)) { this.hidden = true; return; }
        const remaining = Math.ceil((deadline - Date.now()) / 1000);
        const ended = remaining <= 0;
        this.hidden = ended && !this.dataset.expiredText;
        this.toggleAttribute('data-expired', ended);
        digits.hidden = ended;
        expired.hidden = !ended;
        if (ended) return;
        const values = { days: Math.floor(remaining / 86400), hours: Math.floor(remaining % 86400 / 3600), minutes: Math.floor(remaining % 3600 / 60), seconds: remaining % 60 };
        for (const [part, value] of Object.entries(values)) {
          const node = this.querySelector(`[data-countdown-part="${part}"]`);
          const text = String(value).padStart(2, '0');
          if (node && node.textContent !== text) node.textContent = text;
        }
        digits.setAttribute('aria-label', Object.entries(values).map(([part, value]) => `${value} ${this.dataset[`label${part[0].toUpperCase()}${part.slice(1)}`] || part}`).join(', '));
        timer = setTimeout(update, 1000 - Date.now() % 1000 + 10);
      };
      const visible = () => { if (!document.hidden) update(); };
      document.addEventListener('visibilitychange', visible);
      this.cleanup = () => { clearTimeout(timer); document.removeEventListener('visibilitychange', visible); };
      update();
    }
    disconnectedCallback() { this.cleanup?.(); this.started = false; }
  }

  customElements.define('pdp-countdown', PDPCountdown);
})();
